import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet';
import { getImageUrl, hideBrokenImage } from '../utils/imageUrl';

const BlogCategory = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [category, setCategory] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const blogsPerPage = 10;

  const fetchCategory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/categories/${slug}?page=${currentPage}&limit=${blogsPerPage}`);
      setCategory(response.data.category || response.data.data?.category);
      setBlogs(response.data.blogs || response.data.data?.blogs || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Category not found');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, slug]);

  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (!category) {
    return <div className="text-center py-12">Category not found</div>;
  }

  return (
    <>
      <Helmet>
        <title>{category.name} - Blog Category</title>
        <meta name="description" content={category.description} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header Navigation */}
        <header className="bg-white shadow-sm border-b sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center">
                <img 
                  src="/fbrlogo1.svg" 
                  alt="FBR Logo" 
                  className="h-10 w-10 mr-3"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <Link to="/" className="text-2xl font-bold text-blue-600 hover:text-blue-700">
                  FBRSTAPP
                </Link>
              </div>
              <nav className="flex items-center space-x-6">
                <Link to="/" className="text-gray-600 hover:text-gray-900 font-medium">Home</Link>
                <Link to="/blog" className="text-gray-600 hover:text-gray-900 font-medium">Blog</Link>
                {user ? (
                  <Link
                    to="/app/dashboard"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </nav>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <div className="bg-blue-600 text-white py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/blog" className="hover:underline mb-2 inline-block">← Back to Blog</Link>
            <h1 className="text-4xl font-bold mb-2">{category.name}</h1>
            {category.description && <p className="text-lg">{category.description}</p>}
          </div>
        </div>

        {/* Blog Posts */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          {blogs.length > 0 ? (
            <>
              <div className="space-y-6">
                {blogs.map(blog => {
                  const imageUrl = getImageUrl(blog.featured_image);
                  return (
                  <article key={blog.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition">
                    <div className="md:flex">
                      {imageUrl && (
                        <img
                          src={imageUrl}
                          alt={blog.title}
                          className="md:w-1/3 h-64 object-cover"
                          onError={hideBrokenImage}
                        />
                      )}
                      <div className="p-6 flex-1">
                        <Link
                          to={`/blog/${blog.slug}`}
                          className="text-2xl font-bold text-gray-800 hover:text-blue-600"
                        >
                          {blog.title}
                        </Link>
                        <div className="flex gap-4 text-gray-600 text-sm mt-2">
                          <span>By {blog.author_name}</span>
                          <span>{new Date(blog.publish_date).toLocaleDateString()}</span>
                          <span>{blog.views} views</span>
                        </div>
                        <p className="mt-4 text-gray-600 line-clamp-3">
                          {blog.excerpt || blog.content.substring(0, 200)}...
                        </p>
                        <Link
                          to={`/blog/${blog.slug}`}
                          className="inline-block mt-4 text-blue-600 hover:text-blue-800 font-semibold"
                        >
                          Read More →
                        </Link>
                      </div>
                    </div>
                  </article>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-4 py-2 rounded ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-600">
              No blog posts in this category yet.
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogCategory;
